import React, { Suspense, useContext, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import HomePage from "Pages/HomePage";
import NoPage from "Pages/NoPage";
import { PROFILE } from "config";
import { getCookie } from "utils/utils";
import { ReviewContextProvider } from "store/review-context/review-context";
import CircularLoader from "components/UI/loaders/CircularLoader";
import { GTFormContextProvider } from "store/gt-form-context/gt-form-context";
import AuthContext from "store/auth-context/auth-context";
import { useDispatch } from "react-redux";
import { featureFlagActions } from "store/featureFlag/featureFlagSlice";
import useFeatureFlag from "hooks/useFeatureFlag";
import { axiosGu } from "config";
import MainNavigation from "components/navigation/MainNavigation";
import { alertService } from "services/AlertService";
import ProtectedRoute from "components/protectedRoute/ProtectedRoute";
import Redirect from "Pages/Redirect";
import LinearLoader from "components/UI/loaders/LinearLoader";
import styles from "./App.module.css";
import { handleResponseInterceptorError } from "utils/axiosHelper";
const FormPage = React.lazy(() => import("Pages/FormPage"));
const ReviewPage = React.lazy(() => import("Pages/ReviewPage"));

// eslint-disable-next-line react/display-name
const MemoizedApp = React.memo(function ({
  user,
  onLogout,
  isUserDataRequested,
}) {
  return (
    <>
      {user && <MainNavigation user={user} onLogout={onLogout} />}
      <Suspense fallback={<CircularLoader />}>
        <Routes>
          <Route
            path="/redirect"
            element={
              <Redirect user={user} isUserDataRequested={isUserDataRequested} />
            }
          />
          <Route
            element={
              <ProtectedRoute isAllowed={!!user} redirectPath="/redirect" />
            }
          >
            <Route path="/" element={<HomePage />} />
            <Route
              path="/great-telepresence"
              element={
                <GTFormContextProvider>
                  <FormPage />
                </GTFormContextProvider>
              }
            />
          </Route>
          <Route
            element={
              <ProtectedRoute
                isAllowed={!!user && user.roles?.includes("admin")}
                redirectPath="/"
              />
            }
          >
            <Route
              path="/great-telepresence/review"
              element={
                <ReviewContextProvider>
                  <ReviewPage />
                </ReviewContextProvider>
              }
            />
          </Route>

          <Route path="*" element={<NoPage />} />
        </Routes>
      </Suspense>
    </>
  );
});

function App() {
  const {
    user,
    isUserDataRequested,
    getUserData,
    sessionStorageTransferHandler,
    sendLocalStorageSignal,
    formControlers: { logOutHandler },
  } = useContext(AuthContext);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(featureFlagActions.getFeatureFlagData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { isKeycloakIntegrationEnabled } = useFeatureFlag();

  const removeCookie = () => {
    switch (PROFILE) {
      case "development":
        document.cookie =
          "guD=AlertsON; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
        break;
      case "staging":
        document.cookie =
          "guS=AlertsON; path=/; domain=.birdseyesecurity.com; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
        break;
      case "production":
        document.cookie =
          "guP=AlertsON; path=/; domain=.birdseyesecurity.com; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
        break;
      default:
        document.cookie =
          "gu=AlertsON; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    }
  };
  useEffect(() => {
    let cookieLasta;
    let cookieSso;
    let cookieAiq;
    let cookieAlert;
    let cookieGu;

    switch (PROFILE) {
      case "development":
        cookieLasta = getCookie("lastaD");
        cookieSso = getCookie("ssoD");
        cookieAiq = getCookie("aiqD");
        cookieAlert = getCookie("alertD");
        cookieGu = getCookie("guD");
        break;
      case "staging":
        cookieLasta = getCookie("lastaS");
        cookieSso = getCookie("ssoS");
        cookieAiq = getCookie("aiqS");
        cookieAlert = getCookie("alertS");
        cookieGu = getCookie("guS");
        break;
      case "production":
        cookieLasta = getCookie("lastaP");
        cookieSso = getCookie("ssoP");
        cookieAiq = getCookie("aiqP");
        cookieAlert = getCookie("alertP");
        cookieGu = getCookie("guP");
        break;
      default:
        cookieLasta = getCookie("lasta");
        cookieSso = getCookie("sso");
        cookieAiq = getCookie("aiq");
        cookieAlert = getCookie("alert");
        cookieGu = getCookie("gu");
    }

    if (cookieSso || cookieAiq || cookieAlert || cookieGu || cookieLasta) {
      alertService.activate();
      return;
    } else {
      switch (PROFILE) {
        case "development":
          document.cookie = "guD=AlertsON; path=/; ";
          break;
        case "staging":
          document.cookie =
            "guS=AlertsON; path=/; domain=.birdseyesecurity.com; ";
          break;
        case "production":
          document.cookie =
            "guP=AlertsON; path=/; domain=.birdseyesecurity.com; ";
          break;
        default:
          document.cookie = "gu=AlertsON; path=/; ";
      }
      alertService.activate();
    }

    window.addEventListener("beforeunload", removeCookie);
    return () => {
      window.removeEventListener("beforeunload", removeCookie);
      alertService.deactivate();
    };
  }, []);

  useEffect(() => {
    if (!isKeycloakIntegrationEnabled) {
      return;
    }

    const id = axiosGu.interceptors.response.use(
      (response) => response,
      handleResponseInterceptorError
    );

    return () => axiosGu.interceptors.response.eject(id);
  }, [logOutHandler]);

  useEffect(() => {
    window.addEventListener("storage", sessionStorageTransferHandler);
    return () =>
      window.removeEventListener("storage", sessionStorageTransferHandler);
  }, [sendLocalStorageSignal, sessionStorageTransferHandler]);

  useEffect(() => {
    !user && getUserData();
  }, [user, getUserData]);

  if (!isUserDataRequested)
    return (
      <div className={styles["cheking-permission"]}>
        <p>Checking permissions...</p>
        <LinearLoader containerStyles={{ position: "relative" }} />
      </div>
    );

  return (
    <MemoizedApp
      user={user}
      onLogout={logOutHandler}
      isUserDataRequested={isUserDataRequested}
    />
  );
}
export default App;
